import { redirect } from '@tanstack/react-router'
import type { AuthContextType } from '@/contexts/AuthContext'

type GuardArgs = { context: { auth: AuthContextType } }

/**
 * Whether the signed-in user holds a permission.
 *
 * `permissions` already contains the whole catalog for an ADMIN, so there is
 * deliberately no department check here (sc-1215).
 *
 * Looks like `useCan().has()` and is deliberately NOT shared with it. This is
 * a plain function because `beforeLoad` runs outside React and cannot call a
 * hook, and the two differ on the case that matters: guards fail OPEN while
 * the permission set is resolving, `has()` fails CLOSED. Merging them would
 * silently pick one of those behaviours for both.
 */
export function authHasPermission(auth: AuthContextType, key: string): boolean {
  return auth.permissions.includes(key)
}

/** Whether the user holds any permission under a dotted prefix. */
export function authHasAnyPermission(
  auth: AuthContextType,
  prefix: string,
): boolean {
  return auth.permissions.some((permission) => permission.startsWith(prefix))
}

/**
 * Fail-open contract, carried over from the department guards these replace:
 * while the permission set is still in flight (or its fetch errored) we stay
 * put rather than redirect. Bouncing here would kick legitimate users off a
 * cold refresh or deep link, and the client was never the real boundary — the
 * API resolves permissions from the database on every request. `router
 * .invalidate()` in main.tsx re-runs these guards once the fetch settles.
 */
function isResolved(auth: AuthContextType): boolean {
  return auth.permissionsResolved
}

function assertAuthenticated(auth: AuthContextType): void {
  if (!auth.isAuthenticated && !auth.isLoading) {
    throw redirect({ to: '/login' })
  }
}

/** Route guard: requires one specific permission. */
export const requirePermission =
  (key: string) =>
  ({ context }: GuardArgs) => {
    const { auth } = context
    assertAuthenticated(auth)
    if (!isResolved(auth)) return
    if (!authHasPermission(auth, key)) {
      throw redirect({ to: '/home' })
    }
  }

/**
 * Route guard: requires at least one of several specific permissions.
 *
 * For a page hosting tabs with different audiences — the tabs themselves still
 * gate individually, this only decides whether the page is reachable at all.
 */
export const requireAnyOfPermissions =
  (...keys: Array<string>) =>
  ({ context }: GuardArgs) => {
    const { auth } = context
    assertAuthenticated(auth)
    if (!isResolved(auth)) return
    if (!keys.some((key) => authHasPermission(auth, key))) {
      throw redirect({ to: '/home' })
    }
  }

/** Route guard: requires any permission under a prefix. For hub pages. */
export const requireAnyPermission =
  (prefix: string) =>
  ({ context }: GuardArgs) => {
    const { auth } = context
    assertAuthenticated(auth)
    if (!isResolved(auth)) return
    if (!authHasAnyPermission(auth, prefix)) {
      throw redirect({ to: '/home' })
    }
  }
